import {FC, useEffect, useMemo, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button, Form, Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {
  exportTemplate,
  getAllHSDescription,
  getAllHSDescriptionsLevel,
  getAllHSVersions,
  getAllPendingHSDescription,
  importDataMapping,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {ListHSDescriptionRow} from './core/_models'
import {Link} from 'react-router-dom'
import {ListHSCategoriesTable} from './components/ListHSDescriptionTable'
import './MasterDataHSDescriptionPage.scss'
import ModalImportHSDescriptionMapping from './components/ModalImportHSDescriptionMapping'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {Toast, showSwal} from '../../../../ui-kit'
import {getPendingHSDescription} from '../master-data-hs-description-review/core/_requests'

const MasterDataHSDescriptionPage: FC<{subject: string}> = ({subject}) => {
  const intl = useIntl()
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const [isLoading, setIsLoading] = useState(false)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [listHSDescription, setListHSDescription] = useState<ListHSDescriptionRow[] | null>()
  const [listPendingHSDescription, setListPendingHSDescription] = useState<
    ListHSDescriptionRow[] | null
  >()
  const [hsDescriptionParams, setHsDescriptionParams] = useState({
    version: '',
    level: 0,
  })
  const [isShowTable, setIsShowTable] = useState<boolean>(false)
  const [HSVersions, setHSVersions] = useState<any>([])
  const [HSLevels, setHSLevels] = useState<any>([])
  const [countData, setCountData] = useState(0)

  // ---- import modal state ----
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  // ----------------------------

  const fetchHSDescriptionsParams = async () => {
    try {
      setIsLoading(true)
      await getAllHSVersions(axiosPrivate).then((res) => {
        setHSVersions(res.data.versions)
      })

      await getAllHSDescriptionsLevel(axiosPrivate).then((res) => {
        setHSLevels(res.data.levels)
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchHSDescriptionsParams()
  }, [])

  const fetchPendingHSDescription = async () => {
    try {
      const res = await getAllPendingHSDescription(axiosPrivate)
      setListPendingHSDescription(res.data.data)
    } catch (error) {
      console.error(error)
      Toast('Error when fetching HS Description data', 'danger', '')
    }
  }

  useEffect(() => {
    fetchPendingHSDescription()
  }, [])

  const fetchListHSDescription = async (hsDescriptionParams) => {
    setIsTableLoading(true)
    setListHSDescription([])

    try {
      const res = await getAllHSDescription(axiosPrivate, hsDescriptionParams)
      if (res.data.count > 0) {
        setCountData(res.data.count)
        setListHSDescription(res.data.table_data)
      }
    } catch (e) {
      console.error(e)
      Toast('Error when fetching HS Description data', 'danger', '')
    } finally {
      setIsTableLoading(false)
    }
  }

  // Export Template
  const handleExportTemplate = () => {
    exportTemplate(axiosPrivate)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'hs-description-template.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((err) => {
        console.error('Failed to download template:', err)
      })
  }
  // end of Export Template

  // Import Template – open modal
  const handleImportTemplate = () => {
    setShowImportModal(true)
    setImportError(null)
  }

  const handleCloseImportModal = () => {
    if (isImporting) return
    setShowImportModal(false)
    setImportFile(null)
    setImportError(null)
  }

  const handleSubmitImport = () => {
    if (!importFile) {
      setImportError('Please select a file before importing.')
      return
    }

    setIsImporting(true)
    setImportError(null)

    const formData = new FormData()
    formData.append('file', importFile)

    importDataMapping(axiosPrivate, formData)
      .then((res) => {
        if (res.status === 200) {
          setShowImportModal(false)
          showSwal({
            title: 'Import Successful',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            window.location.reload()
          })
        }
      })
      .catch((resErr: any) => {
        const message = resErr?.response?.data?.message || 'Upload failed. Please check your file.'
        setImportError(message)
      })
      .finally(() => {
        setIsImporting(false)
      })
  }
  // end of Import Template

  // search handler
  const isSearchEnabled = useMemo(() => {
    // HS Version is always required
    if (!hsDescriptionParams.version || hsDescriptionParams.level === 0) return false

    return true
  }, [hsDescriptionParams])

  const btnResetHandler = () => {
    setHsDescriptionParams({
      version: '',
      level: 0,
    })
    setIsShowTable(false)
  }

  const btnSearchHandler = async () => {
    setIsShowTable(true)
    await fetchListHSDescription(hsDescriptionParams)
  }
  // end of search handler

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {intl.formatMessage({id: subject})}
      </PageTitle>
      <div className='MasterDataHSPage'>
        {listPendingHSDescription && listPendingHSDescription.length > 0 && (
          <div className='alert-no-result-data'>
            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-start'>
                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1'>Review Needed</h5>
                  <span>There are HS Descriptions that require review or approval:</span>

                  <ul className='mt-2 mb-0'>
                    {listPendingHSDescription.map((legal: ListHSDescriptionRow, index) => (
                      <li key={index}>
                        {ability.can('approve', 'PERM_APPROVE_LEGAL_TINA') ? (
                          <a
                            className='text-link'
                            href={`/master-data-hs-descriptions/review/${legal.id}`}
                          >
                            {legal.id}
                          </a>
                        ) : (
                          <span className='fw-bold'>{legal.id}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        <Row className='mb-5'>
          <Col>
            <Card className='card-custom card-border'>
              <Card.Body className='d-flex align-items-center'>
                <div className='d-flex flex-column'>
                  <h3 className='text-primary'>{intl.formatMessage({id: subject})}</h3>
                  <div className='text-gray-700'>
                    In this module, you can manage available <strong>HS Descriptions</strong>.
                    <br />
                    <br />➤ Use <strong>Add</strong> to create a new description, or{' '}
                    <strong>Edit</strong> to modify an existing one through the{' '}
                    <strong>Detail</strong> page.
                    <br />
                    {ability.can('create', subject) ? (
                      <>
                        ➤ To assign HS Codes to categories in bulk:
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• First, download the Excel format here{' '}
                        <Link
                          to=''
                          className='text-link'
                          onClick={() => {
                            handleExportTemplate()
                          }}
                        >
                          Export Template
                        </Link>
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Fill in the HS Code mapping
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Then upload it here
                        <Button
                          className='btn btn-primary btn-inline'
                          onClick={() => {
                            handleImportTemplate()
                          }}
                        >
                          Import Excel
                        </Button>
                        <br />
                        <i>*Both manual and bulk mapping are supported.</i>
                      </>
                    ) : (
                      <>
                        ➤ To assign HS Codes to categories in bulk, you need to have permission to
                        import Excel.
                      </>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className='mb-5'>
          <Col>
            <Card>
              {!isLoading ? (
                <>
                  <Card.Header className='card-header-stretch'>
                    <Card.Title className='w-100'>
                      <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                        <div className='title d-flex align-items-center'>
                          <span className='fa fa-file-alt px-2'></span>
                          <h3 className='text-gray-700'>List HS Description</h3>
                        </div>
                        {ability.can('create', subject) ? (
                          <div className='card-action'>
                            <Link
                              to={'/master-data-hs-descriptions/add'}
                              className='btn btn-primary d-flex gap-4 align-items-center'
                            >
                              <span className='fa fa-plus'></span>
                              Add
                            </Link>
                          </div>
                        ) : (
                          ''
                        )}
                      </div>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <Row className='mb-5 d-flex align-items-end'>
                      <Col lg={4} md={4}>
                        <Form.Group>
                          <Form.Label>HS Version</Form.Label>
                          <Form.Select
                            className='mt-2 pb-3'
                            size='sm'
                            value={hsDescriptionParams.version || ''}
                            onChange={(e) =>
                              setHsDescriptionParams({
                                ...hsDescriptionParams,
                                version: e.target.value,
                              })
                            }
                          >
                            <option value='' disabled selected>
                              Select HS Version
                            </option>
                            {HSVersions.map((version, key) => (
                              <option key={key} value={version.version}>
                                {version.version}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col lg={4} md={4}>
                        <Form.Group>
                          <Form.Label>Level</Form.Label>
                          <Form.Select
                            className='mt-2 pb-3'
                            size='sm'
                            value={hsDescriptionParams.level || -1}
                            onChange={(e) =>
                              setHsDescriptionParams({
                                ...hsDescriptionParams,
                                level: Number(e.target.value),
                              })
                            }
                          >
                            <option value='-1' disabled selected>
                              Select HS Level
                            </option>
                            {HSLevels.map((level, key) => (
                              <option value={level.level} key={key}>
                                {level.level}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className='mb-5 justify-content-end'>
                      <Col xs='auto'>
                        <Button
                          variant='light'
                          className='me-2'
                          size='sm'
                          onClick={btnResetHandler}
                        >
                          <i className='fa fa-refresh me-1'></i> Reset
                        </Button>
                        <Button
                          variant='primary'
                          size='sm'
                          onClick={btnSearchHandler}
                          disabled={!isSearchEnabled}
                        >
                          <i className='fa fa-search me-1'></i> Search
                        </Button>
                      </Col>
                    </Row>
                    {isShowTable ? (
                      isTableLoading ? (
                        <div
                          className='d-flex align-items-center justify-content-center'
                          style={{height: '50vh'}}
                        >
                          <Spinner
                            animation='border'
                            role='status'
                            variant='primary'
                            className='me-3'
                          />
                          <div className='loading-text'>
                            Loading
                            <span className='dot'>.</span>
                            <span className='dot'>.</span>
                            <span className='dot'>.</span>
                          </div>
                        </div>
                      ) : (
                        <ListHSCategoriesTable
                          subject={subject}
                          onAgreementDelete={(deletedId) => {
                            setListHSDescription((prev) =>
                              prev?.filter((item) => item.id !== deletedId)
                            )
                          }}
                          rows={listHSDescription}
                          isLoading={isLoading}
                        />
                      )
                    ) : (
                      ''
                    )}
                  </Card.Body>
                </>
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{height: '50vh'}}
                >
                  <Spinner animation='border' role='status' variant='primary' className='me-3' />
                  <div className='loading-text'>
                    Loading
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      {/* Import Modal */}
      <ModalImportHSDescriptionMapping
        show={showImportModal}
        onClose={handleCloseImportModal}
        onSubmit={handleSubmitImport}
        onFileChange={setImportFile}
        isSubmitting={isImporting}
        errorMessage={importError}
      />
    </>
  )
}

export default MasterDataHSDescriptionPage
